import { makeStyles, Theme } from '@material-ui/core/styles';

// Local design tokens for the validation dialog. Success/error tones read as
// standard status colours (matching the mockup) while primary actions still use
// the app's themed primary colour.
export const PHOTO_VALIDATION_COLORS = {
  success: '#2e9f5b',
  successBg: '#eef8f1',
  successBorder: '#cdebd8',
  error: '#d3283f',
  errorBg: '#fdeef0',
  errorBorder: '#f4ccd3',
  warning: '#c47a11',
  warningBg: '#fff7eb',
  warningBorder: '#f5e0bc',
  infoBg: '#eef4fd',
  infoBorder: '#d6e4fb',
  info: '#2f6fd1',
  border: '#e4e7eb',
  mutedText: 'rgba(0, 0, 0, 0.6)',
  track: '#e9edf2',
};

export const usePhotoValidationModalStyles = makeStyles((theme: Theme) => ({
  modal: {
    '& .hid-modal__container': {
      maxWidth: 900,
    },
  },
  divider: {
    border: 'none',
    borderTop: `1px solid ${PHOTO_VALIDATION_COLORS.border}`,
    margin: theme.spacing(1, 0, 0),
  },
  paper: {
    borderRadius: 14,
    width: '100%',
    maxWidth: 900,
    margin: theme.spacing(2),
  },
  root: {
    padding: theme.spacing(3, 3, 2),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2, 2, 1.5),
    },
  },
  // Header ------------------------------------------------------------------
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
  },
  headerIcon: {
    color: PHOTO_VALIDATION_COLORS.error,
    fontSize: 30,
    flexShrink: 0,
  },
  headerIconWarning: {
    color: PHOTO_VALIDATION_COLORS.warning,
  },
  headerText: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: 20,
    fontWeight: 600,
    color: theme.palette.text.primary,
    lineHeight: 1.3,
  },
  subtitle: {
    marginTop: theme.spacing(0.5),
    fontSize: 14,
    color: PHOTO_VALIDATION_COLORS.mutedText,
    lineHeight: 1.5,
  },
  closeButton: {
    marginTop: -theme.spacing(1),
    marginRight: -theme.spacing(1),
    color: PHOTO_VALIDATION_COLORS.mutedText,
  },
  // Header failure badge ----------------------------------------------------
  headerBadge: {
    flexShrink: 0,
    width: 64,
    height: 64,
    borderRadius: '50%',
    border: `2px solid ${PHOTO_VALIDATION_COLORS.error}`,
    backgroundColor: PHOTO_VALIDATION_COLORS.errorBg,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    lineHeight: 1.1,
    color: PHOTO_VALIDATION_COLORS.error,
  },
  headerBadgeCount: {
    fontSize: 15,
    fontWeight: 700,
    color: PHOTO_VALIDATION_COLORS.error,
    whiteSpace: 'nowrap',
  },
  headerBadgeLabel: {
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    color: PHOTO_VALIDATION_COLORS.error,
  },
  headerBadgeWarning: {
    borderColor: PHOTO_VALIDATION_COLORS.warning,
    backgroundColor: PHOTO_VALIDATION_COLORS.warningBg,
    color: PHOTO_VALIDATION_COLORS.warning,
  },
  headerBadgeCountWarning: {
    color: PHOTO_VALIDATION_COLORS.warning,
  },
  headerBadgeLabelWarning: {
    color: PHOTO_VALIDATION_COLORS.warning,
  },
  // Body --------------------------------------------------------------------
  body: {
    display: 'flex',
    gap: theme.spacing(3),
    marginTop: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
  },
  leftPanel: {
    flex: '0 0 38%',
    maxWidth: '38%',
    [theme.breakpoints.down('xs')]: {
      flex: '1 1 auto',
      maxWidth: '100%',
    },
  },
  rightPanel: {
    flex: 1,
    minWidth: 0,
  },
  panelHeading: {
    fontSize: 13,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    color: PHOTO_VALIDATION_COLORS.mutedText,
    marginBottom: theme.spacing(1),
  },
  previewImageWrap: {
    width: '100%',
    borderRadius: 10,
    overflow: 'hidden',
    border: `1px solid ${PHOTO_VALIDATION_COLORS.border}`,
    backgroundColor: '#f4f6f8',
    aspectRatio: '1 / 1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  previewImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  previewPlaceholder: {
    color: PHOTO_VALIDATION_COLORS.mutedText,
    fontSize: 13,
    padding: theme.spacing(2),
    textAlign: 'center',
  },
  infoBox: {
    marginTop: theme.spacing(1.5),
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: 10,
    backgroundColor: PHOTO_VALIDATION_COLORS.infoBg,
    border: `1px solid ${PHOTO_VALIDATION_COLORS.infoBorder}`,
  },
  infoIcon: {
    color: PHOTO_VALIDATION_COLORS.info,
    fontSize: 20,
    flexShrink: 0,
  },
  uploadedInfoBox: {
    backgroundColor: PHOTO_VALIDATION_COLORS.successBg,
    border: `1px solid ${PHOTO_VALIDATION_COLORS.successBorder}`,
  },
  uploadedInfoIcon: {
    color: PHOTO_VALIDATION_COLORS.success,
    fontSize: 20,
    flexShrink: 0,
  },
  uploadedInfoText: {
    fontSize: 13,
    color: PHOTO_VALIDATION_COLORS.mutedText,
    lineHeight: 1.5,
  },
  infoTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  infoList: {
    margin: 0,
    paddingLeft: theme.spacing(2),
    color: PHOTO_VALIDATION_COLORS.mutedText,
    fontSize: 13,
    lineHeight: 1.6,
  },
  // Passed checks -----------------------------------------------------------
  sectionHeading: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    fontSize: 14,
    fontWeight: 600,
    margin: theme.spacing(0, 0, 1),
  },
  sectionHeadingPassed: { color: PHOTO_VALIDATION_COLORS.success },
  sectionHeadingFailed: {
    color: PHOTO_VALIDATION_COLORS.error,
  },
  sectionHeadingWarning: {
    color: PHOTO_VALIDATION_COLORS.warning,
  },
  // Applied to whichever check section is rendered second so it is spaced
  // away from the section above it.
  sectionSpacing: {
    marginTop: theme.spacing(2.5),
  },
  passedList: {
    border: `1px solid ${PHOTO_VALIDATION_COLORS.border}`,
    borderRadius: 10,
    overflow: 'hidden',
  },
  passedRow: {
    borderBottom: `1px solid ${PHOTO_VALIDATION_COLORS.border}`,
    '&:last-child': { borderBottom: 'none' },
  },
  passedRowHeader: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 1.5),
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    font: 'inherit',
  },
  passedIcon: {
    color: PHOTO_VALIDATION_COLORS.success,
    fontSize: 20,
    flexShrink: 0,
  },
  passedName: {
    flex: 1,
    fontSize: 14,
    color: theme.palette.text.primary,
  },
  chevron: {
    color: PHOTO_VALIDATION_COLORS.mutedText,
    fontSize: 20,
    transition: 'transform 200ms ease',
  },
  chevronOpen: { transform: 'rotate(180deg)' },
  passedMessage: {
    padding: theme.spacing(0, 1.5, 1.25, 4.5),
    fontSize: 13,
    color: PHOTO_VALIDATION_COLORS.mutedText,
  },
  // Failed checks -----------------------------------------------------------
  failedList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  failedCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: 10,
    backgroundColor: PHOTO_VALIDATION_COLORS.errorBg,
    border: `1px solid ${PHOTO_VALIDATION_COLORS.errorBorder}`,
  },
  warningCard: {
    backgroundColor: PHOTO_VALIDATION_COLORS.warningBg,
    border: `1px solid ${PHOTO_VALIDATION_COLORS.warningBorder}`,
  },
  failedBadge: {
    width: 26,
    height: 26,
    borderRadius: '50%',
    backgroundColor: PHOTO_VALIDATION_COLORS.error,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  warningBadge: {
    backgroundColor: PHOTO_VALIDATION_COLORS.warning,
  },
  failedBadgeIcon: { fontSize: 18 },
  failedContent: { flex: 1, minWidth: 0 },
  failedTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  failedMessage: {
    fontSize: 13,
    color: PHOTO_VALIDATION_COLORS.mutedText,
    marginTop: theme.spacing(0.25),
  },
  colorSwatchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.75),
    flexWrap: 'wrap',
  },
  colorSwatchLabel: {
    fontSize: 13,
    color: PHOTO_VALIDATION_COLORS.mutedText,
  },
  colorSwatch: {
    width: 32,
    height: 32,
    borderRadius: 6,
    flexShrink: 0,
    border: '1px solid rgba(0, 0, 0, 0.18)',
    boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.35)',
  },
  colorSwatchName: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  howToFix: {
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(1),
    borderTop: `1px dashed ${PHOTO_VALIDATION_COLORS.errorBorder}`,
  },
  warningHowToFix: {
    borderTop: `1px dashed ${PHOTO_VALIDATION_COLORS.warningBorder}`,
  },
  howToFixLabel: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    color: PHOTO_VALIDATION_COLORS.error,
  },
  warningHowToFixLabel: {
    color: PHOTO_VALIDATION_COLORS.warning,
  },
  howToFixText: {
    fontSize: 13,
    color: theme.palette.text.primary,
    marginTop: theme.spacing(0.25),
    lineHeight: 1.5,
  },
  failedIllustration: {
    flexShrink: 0,
    color: PHOTO_VALIDATION_COLORS.error,
    opacity: 0.5,
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: { display: 'none' },
  },
  warningIllustration: {
    color: PHOTO_VALIDATION_COLORS.warning,
  },
  failedIllustrationIcon: { fontSize: 34 },
  // Footer ------------------------------------------------------------------
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${PHOTO_VALIDATION_COLORS.border}`,
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column-reverse',
      alignItems: 'stretch',
    },
  },
  replaceButton: {
    textTransform: 'none',
    fontWeight: 600,
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
  },
  okButton: {
    textTransform: 'none',
    fontWeight: 600,
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    '&:hover': { backgroundColor: theme.palette.primary.main },
  },
}));

export default usePhotoValidationModalStyles;
